import { clinic } from "../data/mock";
import { SITE_URL, OG_IMAGE } from "./seoConfig";

const geo = {
  lanka: { lat: 25.2797924, lng: 83.0018369 },
  chetganj: { lat: 25.3212656, lng: 83.0038428 },
};

// Organization schema
export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: clinic.clinicName,
  url: SITE_URL,
  logo: OG_IMAGE,
  telephone: clinic.phone,
  email: clinic.email,
  sameAs: [
    clinic.website,
    clinic.social.instagram,
    clinic.social.facebook,
    clinic.social.youtube,
  ],
});

// Person + Physician schema for the doctor
export const physicianSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["Physician", "Person"],
  "@id": `${SITE_URL}/#physician`,
  name: clinic.doctorName,
  honorificPrefix: "Dr.",
  jobTitle: "Gastroenterologist & Hepatologist",
  medicalSpecialty: ["Gastroenterology", "Hepatology"],
  image: OG_IMAGE,
  url: `${SITE_URL}/about`,
  telephone: clinic.phone,
  email: clinic.email,
  identifier: {
    "@type": "PropertyValue",
    name: "Medical Registration Number",
    value: clinic.registrationNumber,
  },
  hasCredential: [
    { "@type": "EducationalOccupationalCredential", credentialCategory: "MBBS" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "MD (General Medicine)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "DM (Gastroenterology)" },
  ],
  worksFor: { "@id": `${SITE_URL}/#organization` },
  areaServed: { "@type": "City", name: "Varanasi" },
  sameAs: [
    clinic.social.instagram,
    clinic.social.facebook,
    clinic.social.youtube,
  ],
});

// LocalBusiness + MedicalClinic schema (both branches as departments)
export const medicalClinicSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["MedicalClinic", "LocalBusiness"],
  "@id": `${SITE_URL}/#clinic`,
  name: clinic.clinicName,
  image: OG_IMAGE,
  url: SITE_URL,
  telephone: clinic.phone,
  email: clinic.email,
  priceRange: `₹${clinic.consultationFee}`,
  currenciesAccepted: "INR",
  medicalSpecialty: "Gastroenterology",
  address: {
    "@type": "PostalAddress",
    streetAddress: clinic.branches[0].address,
    addressLocality: "Varanasi",
    addressRegion: "Uttar Pradesh",
    addressCountry: "IN",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: geo.lanka.lat,
    longitude: geo.lanka.lng,
  },
  department: clinic.branches.map((b, i) => ({
    "@type": "MedicalClinic",
    name: `${clinic.clinicName} – ${b.name}`,
    address: {
      "@type": "PostalAddress",
      streetAddress: b.address,
      addressLocality: "Varanasi",
      addressRegion: "Uttar Pradesh",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: i === 0 ? geo.lanka.lat : geo.chetganj.lat,
      longitude: i === 0 ? geo.lanka.lng : geo.chetganj.lng,
    },
    openingHours: i === 0 ? "Mo-Su 09:00-17:00" : "Mo-Su 18:00-22:00",
  })),
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: clinic.googleRating || "4.9",
    reviewCount: "180",
  },
  sameAs: [
    clinic.website,
    clinic.social.instagram,
    clinic.social.facebook,
    clinic.social.youtube,
  ],
});

// FAQ schema
export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// Breadcrumb schema
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: (items || []).map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path === "/" ? "/" : it.path + "/"}`,
  })),
});

// MedicalCondition schema
export const medicalConditionSchema = (condition, procedure) => {
  const node = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    name: condition.name,
    alternateName: condition.alternateName,
    associatedAnatomy: { "@type": "AnatomicalStructure", name: "Liver" },
  };
  if (procedure) {
    node.possibleTreatment = {
      "@type": procedure.type || "MedicalTherapy",
      name: procedure.name,
    };
  }
  return node;
};

// Review schema (uses first few reviews)
export const reviewSchema = (reviews) => ({
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  "@id": `${SITE_URL}/#clinic`,
  name: clinic.clinicName,
  review: (reviews || []).slice(0, 4).map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.name },
    reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
    reviewBody: r.text,
  })),
});
